// useDictOptions — 給「列表篩選下拉 + 表格類型欄」讀 system_dictionary 的小 hook。
//
// 為什麼存在: fund_type / project_type 這種「可由字典維護」的列舉, 建立表單走
// ReferenceSelect (已讀字典), 但列表頁的篩選下拉 + 表格「類型」欄長期用寫死的
// OPTIONS/LABEL map → 你在字典新增一個類型, 建立表單看得到、列表卻不認得
// (2026-07-16 字典審計抓到)。這個 hook 讓列表也讀同一份字典。
//
// 快取 key 對齊 ReferenceSelect (`['ref', type, false, '', actor]`) → 同一份
// 字典抓一次共用。fallback 傳「模組層級常數」(穩定 identity), 在字典載入中 /
// 抓取失敗時 labelOf 仍給得出中文、不會露出 raw code。
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useActor } from './actor-store.ts';
import { api } from './api.ts';

interface DictRow {
  code: string;
  label_zh: string | null;
  label_en: string | null;
  sort_order: number;
}

const EMPTY_FALLBACK: Record<string, string> = {};

export function useDictOptions(type: string, fallback: Record<string, string> = EMPTY_FALLBACK) {
  const actor = useActor((s) => s.actor);
  const q = useQuery<DictRow[]>({
    // key 對齊 ReferenceSelect 的 active-only 抓取 → 共用快取
    queryKey: ['ref', type, false, '', actor],
    queryFn: () =>
      api<DictRow[]>(
        `/api/v1/master-data?type=${encodeURIComponent(type)}&include_inactive=false`,
        {
          actor,
        },
      ),
    staleTime: 5 * 60 * 1000,
  });

  const rows = q.data;
  return useMemo(() => {
    const labelMap: Record<string, string> = { ...fallback };
    const options: { value: string; label: string }[] = [];
    if (rows && rows.length > 0) {
      for (const r of rows) {
        const label = r.label_zh ?? r.label_en ?? r.code;
        labelMap[r.code] = label;
        options.push({ value: r.code, label });
      }
    } else {
      // 字典還沒回來 (或失敗) → 用 fallback 撐住篩選下拉, 順序照 fallback 定義
      for (const [value, label] of Object.entries(fallback)) options.push({ value, label });
    }
    return {
      options,
      labelMap,
      labelOf: (code: string | null | undefined) => (code ? (labelMap[code] ?? code) : '—'),
      isLoading: q.isLoading,
    };
  }, [rows, fallback, q.isLoading]);
}

/** 幣別下拉的 fallback — 字典還沒回來(或失敗)時撐住表單, 順序即顯示順序。
 *  2026-08-21 字典審計前, 這份清單以不同子集散在 5 個檔案裡 —— 你在字典新增
 *  一個幣別, 只有讀字典的那幾張表單看得到。 收斂成: 一律讀字典, 這份只當 fallback。 */
export const CURRENCY_CODES_FALLBACK = ['TWD', 'USD', 'EUR', 'JPY', 'CNY', 'HKD', 'GBP', 'SGD'];

/** 幣別代碼清單 (讀字典 type='currency', 空/失敗時退回 fallback)。
 *  選項顯示維持代碼 (TWD) 不帶中文 — 跟既有表單一致, 錢的欄位旁邊擺代碼是慣例。 */
export function useCurrencyCodes(): string[] {
  const { options } = useDictOptions('currency');
  return options.length > 0 ? options.map((o) => o.value) : CURRENCY_CODES_FALLBACK;
}
